<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'

const props = defineProps({
	modelValue: {
		default: null,
	},
	options: {
		type: Array,
		default: () => [],
	},
	divideindex: {
		type: Number,
		default: -1,
	},
	searchable: {
		type: Boolean,
		default: false,
	},
	searchProperties: {
		type: Array,
		default: () => ['title', 'type', 'description'],
	},
	remoteSearch: {
		type: Function,
		default: null,
	},
	minSearchLength: {
		type: Number,
		default: 2,
	},
	searchDebounce: {
		type: Number,
		default: 300,
	},
	rememberSearchQuery: {
		type: Boolean,
		default: false,
	},
	noWrap: {
		type: Boolean,
		default: false,
	},
	cols: {
		type: Boolean,
		default: false,
	},
	multiple: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['update:modelValue', 'change', 'open'])
const open = ref(false)
const searchQuery = ref('')
const isLoading = ref(false)
const remoteOptions = ref([])
const debounceTimeout = ref(null)
const searchInputRef = ref(null)
const lastSearchQuery = ref('')
const selectBoxRef = ref(null)

// Create a unique ID for this select box instance
const instanceId = ref(`select-box-${Date.now()}-${Math.floor(Math.random() * 1000)}`)

// Helper function to check if an option is selected
const isOptionSelected = (option) => {
	if (props.multiple) {
		// If modelValue is an array, check if the option is in the array
		return Array.isArray(props.modelValue) && props.modelValue.includes(option)
	} else {
		// Single select mode
		return option === props.modelValue
	}
}

const selectOption = (option, event) => {
	if (props.multiple) {
		// In multiple mode, we need to toggle the selection
		let newValue = []

		// Make sure we're always working with an array
		const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : []

		if (isOptionSelected(option)) {
			// If already selected, remove it
			newValue = currentValues.filter((item) => item !== option)
		} else {
			// If not selected, add it
			newValue = [...currentValues, option]
		}

		emit('update:modelValue', newValue)
		emit('change', newValue)

		// In multiple mode, clicking an option doesn't close the dropdown
		event?.stopPropagation()
	} else {
		// Single select mode behavior remains the same
		emit('update:modelValue', option)
		emit('change', option)
		open.value = false
	}

	// Only clear search query and results if not set to remember it and in single select mode
	if (!props.rememberSearchQuery && !props.multiple) {
		searchQuery.value = ''
		remoteOptions.value = []
	}
}

// Extracted the dropdown opening/closing logic to a separate function
const openDropdown = () => {
	// Dispatch custom event to close other select boxes
	window.dispatchEvent(
		new CustomEvent('close-all-select-boxes', {
			detail: { exceptId: instanceId.value },
		}),
	)

	open.value = true
	emit('open', true)

	// When opening the dropdown
	// Show prefilled options by ensuring remoteOptions is empty when focused
	if (!searchQuery.value || searchQuery.value.length < props.minSearchLength) {
		remoteOptions.value = []
	}

	// If searchable, focus the input after rendering
	if (props.searchable) {
		nextTick(() => {
			if (searchInputRef.value) {
				searchInputRef.value.focus()
			}
		})
	}
}

const closeDropdown = () => {
	open.value = false
	emit('open', false)
	if (!props.rememberSearchQuery) {
		// Only clear search results and query if we're not remembering
		remoteOptions.value = []
		searchQuery.value = ''
	}
}

const toggleDropdown = () => {
	if (!open.value) {
		openDropdown()
	} else {
		closeDropdown()
	}
}

// Modified to separate DOM click handler from the toggle logic
const handleOutsideClick = (event) => {
	if (!selectBoxRef.value?.contains(event.target)) {
		if (open.value) {
			emit('open', false)
		}
		open.value = false

		if (!props.rememberSearchQuery) {
			// Only clear search results and query if we're not remembering
			remoteOptions.value = []
			searchQuery.value = ''
		}
	}
}

// Handle tag removal without affecting dropdown state
const removeTag = (option, event) => {
	// Prevent the click from bubbling to parent elements
	event.stopPropagation()

	// Remove the item from selection
	if (props.multiple && Array.isArray(props.modelValue)) {
		const newValue = props.modelValue.filter((item) => item !== option)
		emit('update:modelValue', newValue)
		emit('change', newValue)
	}
}

// Handler for the custom event to close all select boxes except one
const handleCloseAllSelectBoxes = (event) => {
	// If this is not the select box that should stay open, close it
	if (event.detail.exceptId !== instanceId.value && open.value) {
		open.value = false

		if (!props.rememberSearchQuery) {
			remoteOptions.value = []
			searchQuery.value = ''
		}
	}
}

const performRemoteSearch = async () => {
	if (!props.remoteSearch || searchQuery.value.length < props.minSearchLength) {
		remoteOptions.value = []
		return
	}

	// Always clear previous results when query changes
	remoteOptions.value = []

	try {
		isLoading.value = true
		const results = await props.remoteSearch(searchQuery.value)
		remoteOptions.value = Array.isArray(results) ? results : []
		lastSearchQuery.value = searchQuery.value
	} catch (error) {
		console.error('Remote search error:', error)
		remoteOptions.value = []
	} finally {
		isLoading.value = false
	}
}

const debouncedSearch = () => {
	if (debounceTimeout.value) {
		clearTimeout(debounceTimeout.value)
	}

	debounceTimeout.value = setTimeout(() => {
		performRemoteSearch()
	}, props.searchDebounce)
}

watch(searchQuery, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		if (props.remoteSearch) {
			// Clear results immediately when search query changes
			remoteOptions.value = []
			debouncedSearch()
		}
	}
})

// Watch for changes in the modelValue prop
watch(
	() => props.modelValue,
	(newValue, oldValue) => {
		if (newValue !== oldValue && !props.multiple) {
			// Clear remote options and search query when the model value changes
			// Only in single select mode
			remoteOptions.value = []
			searchQuery.value = ''
		}
	},
)

const filteredOptions = computed(() => {
	// Ensure options is an array to prevent filter errors
	const safeOptions = Array.isArray(props.options) ? props.options : []

	// If we have remote search results, prioritize them over local options
	if (remoteOptions.value && remoteOptions.value.length > 0) {
		return remoteOptions.value
	}

	// When no remote search results but we have a search query
	if (props.remoteSearch && searchQuery.value && searchQuery.value.length >= props.minSearchLength) {
		// If we're actively searching remotely, return empty array or show a loading state
		return isLoading.value ? [] : []
	}

	// For empty search query or when not using remote search, filter local options
	if (!searchQuery.value) return safeOptions

	// Filter local options using the specified searchProperties
	return safeOptions.filter((option) => {
		if (!option) return false

		const query = searchQuery.value.toLowerCase()

		// Check if any of the specified properties match the query
		return props.searchProperties.some((property) => {
			const value = option[property]?.toLowerCase() || ''
			return value.includes(query)
		})
	})
})

onMounted(() => {
	document.addEventListener('click', handleOutsideClick)
	// Listen for the custom event to close all select boxes
	window.addEventListener('close-all-select-boxes', handleCloseAllSelectBoxes)
})

onUnmounted(() => {
	document.removeEventListener('click', handleOutsideClick)
	window.removeEventListener('close-all-select-boxes', handleCloseAllSelectBoxes)
	if (debounceTimeout.value) {
		clearTimeout(debounceTimeout.value)
	}
})
</script>

<template>
	<div ref="selectBoxRef" class="select-box" :class="{ nowrap: noWrap, cols: cols, multiple, single: !multiple }" :data-instance-id="instanceId" @click="toggleDropdown">
		<!-- Modified to use @click on the entire component instead of using @blur -->
		<div class="selected" :class="{ open }">
			<!-- Show selected-none when no value is selected (in both single and multiple modes) -->
			<template v-if="!modelValue || (Array.isArray(modelValue) && modelValue.length === 0)">
				<slot name="selected-none">
					<div class="placeholder">Velg...</div>
				</slot>
			</template>

			<!-- Use selected-multiple slot only when multiple is true AND at least one item is selected -->
			<template v-else-if="multiple && Array.isArray(modelValue) && modelValue.length > 0">
				<slot name="selected-multiple" :selectedValues="modelValue">
					<div class="selected-tags">
						<div v-for="(option, index) in modelValue" :key="index" class="selected-tag">
							<slot name="tag" :option="option">
								{{ option.title || option.name || option }}
							</slot>
							<!-- Modified to use removeTag function with stop propagation -->
							<div class="tag-remove" @click="removeTag(option, $event)">&times;</div>
						</div>
					</div>
				</slot>
			</template>

			<!-- Use selected-content slot only for single selection when a value is selected -->
			<template v-else>
				<slot name="selected-content" :selectedValue="modelValue">
					<div>
						{{ modelValue.title || modelValue.name || modelValue }}
					</div>
				</slot>
			</template>
		</div>
		<div class="items" :class="{ selectHide: !open }">
			<div class="container">
				<div v-if="multiple" class="close" @click.stop="closeDropdown()">
					<i class="fas fa-times"></i>
				</div>
				<input v-if="searchable" v-model="searchQuery" ref="searchInputRef" type="text" class="search-input" placeholder="Søk..." @input="remoteSearch ? debouncedSearch : null" />
				<div v-if="isLoading" class="loading-indicator">Søker...</div>
				<div v-else-if="searchQuery && searchQuery.length >= minSearchLength && filteredOptions.length === 0" class="no-results">Ingen resultater funnet</div>
				<div
					v-for="(option, i) of filteredOptions"
					:key="i"
					class="item"
					:class="{
						selected: isOptionSelected(option),
						divide: i === divideindex,
					}"
					@click.stop="selectOption(option, $event)"
				>
					<div v-if="multiple" class="checkbox">
						<input type="checkbox" :checked="isOptionSelected(option)" />
					</div>
					<slot name="item" :option="option"></slot>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.loading-indicator {
	padding: 1rem;
	text-align: center;
	color: #666;
	background-color: white;
	display: flex;
	gap: 1rem;
	justify-content: center;
	align-items: center;
}

.no-results {
	padding: 10px;
	text-align: center;
	color: #666;
}

.multiple .item {
	display: flex;
	align-items: center;
}

.multiple .item:first-child {
	background: white url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"%3E%3Cpath fill="%236c757d" d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"/%3E%3C/svg%3E') right 0.75rem center/0.75rem no-repeat;
}

.checkbox {
	margin-right: 8px;
	display: flex;
	align-items: center;
}

.checkbox input[type='checkbox'] {
	border-radius: 3px;
	border-color: #999;
}

.checkbox input[type='checkbox']:not(:checked) {
	background-color: #f0f0f0;
}

.checkbox input[type='checkbox']:checked {
	border: none;
}

.select-box.multiple .item:hover {
	cursor: pointer;
}

.select-box.multiple .close {
	position: absolute;
	right: 0px;
	top: 0px;
	background-color: rgba(0, 0, 0, 0.05);
	line-height: 1.25rem;
	font-size: 1.125rem;
	padding: 1rem;
	padding-top: 0.75rem;
	padding-bottom: 0.75rem;
	border-top-right-radius: 6px;
	color: #6c757d;
}

.select-box.multiple input {
	pointer-events: none;
}

/* Selected tags styling */
.selected-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-left: -0.25rem;
}

.selected-tag {
	display: flex;
	align-items: center;
	background-color: #ddd;
	border-radius: 0.5rem;
	padding: 3px 8px;
	font-size: 0.9em;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
}

.tag-remove {
	display: inline-block;
	margin-left: 6px;
	font-size: 1.25em;
	opacity: 0.5;
}

.placeholder {
	color: #999;
}
</style>
